import { useEffect, useState } from 'preact/hooks';
import { SourceCitationChip } from '../components/SourceCitationChip';
import { InterpretationLabel } from '../components/InterpretationLabel';
import { ProposeCard } from '../components/ProposeCard';
import { AttentionTile, type AttentionSeverity } from '../components/AttentionTile';
import { GlanceHeader } from '../components/GlanceHeader';
import {
  listAttentionItems,
  listProposals,
  approveProposal,
  rejectProposal,
  ApiError,
  type AttentionItem,
  type Proposal,
} from '../lib/api';
import { resolveAttentionDestination } from '../lib/attentionRoute';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import './Today.css';

export interface TodayProps {
  tenantId: string;
  /** Real session data (App.tsx derives this from the signed-in user) —
   *  the this-is-me glance header names the actual person, never a
   *  placeholder. */
  userName: string;
  /** First-run empty-state invitations (Auth-2). When the workspace has
   *  nothing pending, Today offers a way IN rather than a blank screen:
   *  add a project, or forward a breakdown (the capture flow). Optional so
   *  existing callers/tests keep working — the tiles simply don't render. */
  onAddProject?: () => void;
  onForwardBreakdown?: () => void;
  /** R4 (Actionable Everywhere): how a doorway tile reaches a record's own
   *  screen/section. Attention tiles whose subject resolves to the record
   *  EDITOR open it directly (no nav needed); tiles that resolve to a detail
   *  screen or a section route through here. Optional so existing callers/
   *  tests keep working — a route-kind tile simply stays inert without it,
   *  while an editor-kind tile still opens. */
  onNavigate?: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

// Server severities (attention/riskEngine.ts's ATTENTION_SEVERITIES: info/
// warning/critical) onto AttentionTile's own 5-value scale. 'neutral'/
// 'good' have no server-side equivalent — only real attention items ever
// reach this map, so those two are simply unused here, not a gap.
const SEVERITY_MAP: Record<AttentionItem['severity'], AttentionSeverity> = {
  info: 'neutral',
  warning: 'warning',
  critical: 'critical',
};

// A domain glyph per item type (AttentionTile's `icon` prop — never the
// severity signal itself, see that component's own doc). Deliberately
// modest: a fallback covers any type this list doesn't special-case rather
// than guessing at a more specific glyph with no real domain data behind it.
const ATTENTION_ICON: Partial<Record<AttentionItem['itemType'], string>> = {
  overdue: '!',
  due_soon: '~',
  silence: '…',
  drift: '≈',
  watcher: '◇',
  booking_upcoming: '▸',
  instance_due: '✓',
  credential_expiring: '⚠',
  low_stock: '▤',
  no_show: '✕',
  malware_detected: '⚠',
  scan_unavailable: '?',
};

// What a proposal's `kind` honestly tells us about its provenance without
// a second round-trip to resolve sourceRef into a real file/record name
// (out of scope this chunk — read-only Today, see this file's own A2.1b
// header note) — never invents a specific source, just names the kind.
const KIND_LABEL: Record<string, string> = {
  extraction: 'from a captured source',
  watcher: 'raised by a watcher',
  system: 'system-raised',
  commitment_close: 'closes an existing commitment',
};

const LIVE_WINDOW_MS = 5 * 60 * 1000;

function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function isLive(iso: string): boolean {
  const then = new Date(iso).getTime();
  return !Number.isNaN(then) && Date.now() - then < LIVE_WINDOW_MS;
}

function proposalTitle(p: Proposal): string {
  const title = p.proposed?.['title'];
  return typeof title === 'string' && title.length > 0 ? title : 'A proposal needs a look';
}

// The default tab. Attention-first: ONE proposal foregrounded, the next
// item named but visually secondary — the system does the triage, never a
// wall of tiles (locked, p7-shell-locked). A2.1b: reads the real attention
// + pending-proposals endpoints (mapped in lib/api.ts); Approve/Correct/
// Not-now stay wired to a no-op — the WRITE loop is the next chunk.
export function Today({ tenantId, userName, onAddProject, onForwardBreakdown, onNavigate }: TodayProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [attentionItems, setAttentionItems] = useState<AttentionItem[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // A local nonce the inline proposal decision bumps to re-pull the stream, plus
  // the app-wide records version so acting on a record from a doorway tile (or
  // anywhere else) refreshes Today's attention too.
  const [reloadNonce, setReloadNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState('loading');
      try {
        const [attention, pending] = await Promise.all([
          listAttentionItems(tenantId),
          listProposals(tenantId, 'pending'),
        ]);
        if (cancelled) return;
        setAttentionItems(attention);
        setProposals(pending);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('error');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, reloadNonce, recordsVersion]);

  // The inline propose->approve primitive: Approve materializes the commitment,
  // Not now rejects it — both resolve the decision on the card and re-pull the
  // stream so the settled proposal drops away and the next thing surfaces.
  async function decideProposal(id: string, decision: 'approve' | 'reject'): Promise<void> {
    try {
      if (decision === 'approve') await approveProposal(tenantId, id);
      else await rejectProposal(tenantId, id);
    } catch (err) {
      if (err instanceof ApiError) setErrorMessage(err.message);
    } finally {
      setReloadNonce((n) => n + 1);
    }
  }

  // Turn one attention item into its doorway action, or undefined for a tile
  // whose subject we can't resolve to a destination (it then renders inert —
  // graceful, never a dead role="button"). Editor-kind destinations open the
  // record editor directly; route-kind destinations need onNavigate.
  function attentionAction(item: AttentionItem): (() => void) | undefined {
    const dest = resolveAttentionDestination(item);
    if (!dest) return undefined;
    if (dest.kind === 'editor') {
      return () => openRecordEditor({ mode: 'edit', typeKey: dest.typeKey, id: dest.id });
    }
    return onNavigate ? () => onNavigate(dest.path) : undefined;
  }

  if (state === 'loading') {
    return (
      <div class="today">
        <GlanceHeader name={userName} />
        <div class="today__eyebrow">Today</div>
        <div class="today__say">One moment…</div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div class="today">
        <GlanceHeader name={userName} />
        <div class="today__eyebrow">Today</div>
        <div class="today__say">
          Couldn't load today.
          <br />
          <span class="today__say-soft">{errorMessage}</span>
        </div>
      </div>
    );
  }

  const leadProposal = proposals[0];
  const leadAttention = attentionItems[0];

  if (!leadProposal && !leadAttention) {
    // Nothing pending. Rather than a blank/error screen, invite a way IN —
    // add a project, or forward a breakdown (the capture flow). The
    // invitations only render when App wired the handlers (first-run Today);
    // an already-active user with a quiet day still sees the calm "caught up".
    const canInvite = Boolean(onAddProject || onForwardBreakdown);
    return (
      <div class="today">
        <GlanceHeader name={userName} />
        <div class="today__eyebrow">Today</div>
        <div class="today__say">
          {canInvite ? 'Nothing needs you yet.' : 'Nothing needs you right now.'}
          <br />
          <span class="today__say-soft">
            {canInvite
              ? 'Forward a breakdown or add a project to begin.'
              : "You're caught up."}
          </span>
        </div>
        {onAddProject ? (
          <button type="button" class="today__invite" onClick={onAddProject}>
            <span class="today__invite-glyph" aria-hidden="true">
              +
            </span>
            <span class="today__invite-text">
              <span class="today__invite-title">Add your first project</span>
              <span class="today__invite-sub">a film, series, or play you're working on</span>
            </span>
          </button>
        ) : null}
        {onForwardBreakdown ? (
          <button type="button" class="today__invite" onClick={onForwardBreakdown}>
            <span class="today__invite-glyph" aria-hidden="true">
              ✉
            </span>
            <span class="today__invite-text">
              <span class="today__invite-title">Forward a breakdown</span>
              <span class="today__invite-sub">an audition email or self-tape request</span>
            </span>
          </button>
        ) : null}
      </div>
    );
  }

  const sayLead = leadProposal && leadAttention ? 'Two things need you.' : 'One thing needs you.';
  const attentionTitle = leadAttention ? leadAttention.reason ?? leadAttention.itemType.replace(/_/g, ' ') : null;

  return (
    <div class="today">
      <GlanceHeader name={userName} />
      <div class="today__eyebrow">Today</div>
      <div class="today__say">
        {sayLead}
        <br />
        <span class="today__say-soft">Here's the first — the rest can wait.</span>
      </div>

      {leadProposal ? (
        <ProposeCard
          citation={
            <SourceCitationChip
              glyph={isLive(leadProposal.createdAt) ? '◆' : undefined}
              source={KIND_LABEL[leadProposal.kind] ?? leadProposal.kind}
              when={relativeWhen(leadProposal.createdAt)}
              live={isLive(leadProposal.createdAt)}
            />
          }
          title={proposalTitle(leadProposal)}
          meta={
            leadProposal.rationale ? (
              <InterpretationLabel kind="interpreted">{leadProposal.rationale}</InterpretationLabel>
            ) : (
              <InterpretationLabel kind="interpreted">
                {leadProposal.confidence !== null
                  ? `${Math.round(leadProposal.confidence * 100)}% confidence`
                  : 'unrated confidence'}
              </InterpretationLabel>
            )
          }
          fileTarget={<>{KIND_LABEL[leadProposal.kind] ?? leadProposal.kind}</>}
          onApprove={() => void decideProposal(leadProposal.id, 'approve')}
          // Correct = open the proposed record to adjust before it's filed:
          // edit its existing target if it already points at one, else a
          // create form pre-seeded with the proposed fields.
          onCorrect={() =>
            openRecordEditor(
              leadProposal.targetId
                ? { mode: 'edit', typeKey: leadProposal.targetType, id: leadProposal.targetId }
                : { mode: 'create', typeKey: leadProposal.targetType, prefillFields: leadProposal.proposed }
            )
          }
          onNotNow={() => void decideProposal(leadProposal.id, 'reject')}
        />
      ) : null}

      {leadAttention ? (
        <AttentionTile
          severity={SEVERITY_MAP[leadAttention.severity]}
          icon={ATTENTION_ICON[leadAttention.itemType] ?? '•'}
          // "Then: …" carries the queue-position framing (this WAS "Next"
          // as the tile's corner word — an A2.1a deviation fixed here per
          // this chunk's brief: severity is conveyed by the corner WORD
          // (below, left at its default), never colour alone, and never
          // overloaded with a sequencing word instead).
          title={leadProposal ? `Then: ${attentionTitle}` : (attentionTitle as string)}
          sub={relativeWhen(leadAttention.lastRaisedAt)}
          onClick={attentionAction(leadAttention)}
        />
      ) : null}
    </div>
  );
}
